import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import sanctionsService from '../../services/sanctions.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import type { QueueItem, QueuePage, SanctionDisposition, ScreeningHit } from '../../types/sanctions';
import { t } from '../../i18n/language';

/**
 * Sanctions screening queue — the officer-facing surface for potential list
 * matches (the OFAC, United Nations and Canadian lists) raised by onboarding, money
 * movement and the periodic sweep.
 *
 * One row per subject and list entry, with its screenings beneath (#305): a retried
 * screening or one against a new publication of the same entry joins its item instead
 * of adding a row. The list pages and says how many items and screenings the whole
 * queue holds. What the system closed itself (a standing four-eyes clearance of the
 * same entry, a stale-forced review followed by a clear screening) is not here.
 *
 * Two things make this heavier than the AML alert queue:
 *  · The reviewer judges same-person-or-not, so each row expands to the matched
 *    LIST CANDIDATES (name, source, list uid, score) — not just a number.
 *  · Confirming a true match auto-suspends the client (0038/S2). That action is
 *    behind a typed "CONFIRM SUSPENSION" gate and is impossible on a
 *    stale-forced review (a snapshot was stale, so there is no real match to
 *    confirm — the API refuses it too).
 */

const SUSPENSION_PHRASE = 'CONFIRM SUSPENSION';

function hasProposal(hit: ScreeningHit): boolean {
  return hit.dispositions.some((d) => d.disposition === 'proposed_false_positive');
}

/** Flash text after a disposition is recorded. */
function dispositionMessage(
  disposition: SanctionDisposition, subject: string, suspended: boolean,
): string {
  if (suspended) return `True match confirmed — ${subject}'s account is suspended.`;
  switch (disposition) {
    case 'false_positive': return `Cleared ${subject} as a false positive.`;
    case 'proposed_false_positive':
      return `False positive proposed for ${subject} — a second reviewer must confirm.`;
    case 'escalated': return `Escalated ${subject} to senior compliance.`;
    default: return `Recorded the decision for ${subject}.`;
  }
}

interface ActionTarget {
  hit: ScreeningHit;
  disposition: SanctionDisposition;
}

// ─── Candidate + history panel (expanded row) ─────────────────────
interface DetailProps {
  hit: ScreeningHit;
  onAction: (t: ActionTarget) => void;
}

function ScreeningDetail({ hit, onAction }: Readonly<DetailProps>): JSX.Element {
  const proposed = hasProposal(hit);
  return (
    <div className="px-4 py-4" style={{ background: colors.canvas }}>
      {hit.is_stale_forced && (
        <div
          className="mb-3 p-3 rounded-lg text-sm"
          style={{ background: colors.warningBg, border: `1px solid ${colors.warning}`, color: colors.warning }}
        >
          <strong>Review forced by a stale list snapshot.</strong> This screening was
          opened because the sanctions-list snapshot was missing or out of date — it is{' '}
          <em>not</em> a confirmed identity match. Do not assume a match. Confirming a
          true match is disabled until the list is refreshed and the subject re-screened.
        </div>
      )}

      <div className="text-xs font-medium mb-2" style={{ color: colors.muted }}>
        Matched list entries
      </div>
      {hit.matches.length === 0 ? (
        <p className="text-sm mb-3" style={{ color: colors.mutedSoft }}>
          No candidate entries — nothing on the list matched the name.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg mb-3" style={{ border: `1px solid ${colors.borderSoft}` }}>
          <table className="w-full text-xs">
            <thead style={{ background: colors.surfaceMuted, color: colors.muted }}>
              <tr>
                <th className="text-left font-medium px-3 py-2">Listed name</th>
                <th className="text-left font-medium px-3 py-2">Source</th>
                <th className="text-left font-medium px-3 py-2">List UID</th>
                <th className="text-right font-medium px-3 py-2">Score</th>
              </tr>
            </thead>
            <tbody>
              {hit.matches.map((m) => (
                <tr key={m.entry_id} style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
                  <td className="px-3 py-2" style={{ color: colors.ink }}>{m.name}</td>
                  <td className="px-3 py-2">
                    <StatusBadge tone="info">{m.source}</StatusBadge>
                  </td>
                  <td className="px-3 py-2 font-mono" style={{ color: colors.muted }}>{m.source_uid}</td>
                  <td className="px-3 py-2 text-right font-mono" style={{ color: colors.ink }}>
                    {m.score.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hit.dispositions.length > 0 && (
        <>
          <div className="text-xs font-medium mb-2" style={{ color: colors.muted }}>
            Review history
          </div>
          <ul className="mb-3 space-y-1">
            {hit.dispositions.map((d) => (
              <li key={`${d.disposition}-${d.decided_at}`} className="text-xs" style={{ color: colors.inkSoft }}>
                <span className="font-medium">{d.disposition.replace(/_/g, ' ')}</span>
                {' · '}
                {formatDateTime(d.decided_at)} — {d.reason}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex flex-wrap gap-2">
        {proposed ? (
          <button
            type="button"
            onClick={() => onAction({ hit, disposition: 'false_positive' })}
            className="px-3 py-1.5 rounded-md text-xs font-semibold"
            style={{ background: gradients.gold, color: colors.navy }}
          >
            Confirm false positive
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onAction({ hit, disposition: 'proposed_false_positive' })}
            className="px-3 py-1.5 rounded-md text-xs font-semibold"
            style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
          >
            Propose false positive
          </button>
        )}
        <button
          type="button"
          disabled={hit.is_stale_forced}
          title={hit.is_stale_forced ? 'Disabled — review forced by a stale snapshot' : undefined}
          onClick={() => onAction({ hit, disposition: 'confirmed_true_match' })}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
            opacity: hit.is_stale_forced ? 0.4 : 1,
            cursor: hit.is_stale_forced ? 'not-allowed' : 'pointer',
          }}
        >
          Confirm true match
        </button>
        {!hit.is_escalated && (
          <button
            type="button"
            onClick={() => onAction({ hit, disposition: 'escalated' })}
            className="px-3 py-1.5 rounded-md text-xs font-semibold"
            style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
          >
            Escalate
          </button>
        )}
      </div>
    </div>
  );
}

// ─── An item's screenings, each decided on its own ────────────────
interface ItemDetailProps {
  item: QueueItem;
  onAction: (t: ActionTarget) => void;
}

function ItemDetail({ item, onAction }: Readonly<ItemDetailProps>): JSX.Element {
  return (
    <div style={{ background: colors.canvas }}>
      {item.screenings.map((hit, idx) => (
        <div
          key={hit.result_id}
          style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
        >
          <div className="px-4 pt-3 text-xs font-medium" style={{ color: colors.muted }}>
            {`Screening ${hit.result_id} · ${hit.context} · ${formatDateTime(hit.screened_at)}`}
          </div>
          <ScreeningDetail hit={hit} onAction={onAction} />
        </div>
      ))}
    </div>
  );
}

function pageCount(data: QueuePage | null): number {
  if (!data) return 1;
  return Math.max(1, Math.ceil(data.total_items / data.page_size));
}

export default function SanctionsScreenings(): JSX.Element {
  const [data, setData] = useState<QueuePage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [target, setTarget] = useState<ActionTarget | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const found = await sanctionsService.listQueue(page);
      // The last item of the last page was decided: show the page that is now the last.
      if (found.items.length === 0 && page > pageCount(found)) {
        setPage(pageCount(found));
        return;
      }
      setData(found);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load sanctions screenings.'));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { void reload(); }, [reload]);

  const items = data?.items ?? [];
  const totalPages = pageCount(data);

  const strongestMatchCell = (item: QueueItem): JSX.Element => {
    if (item.is_stale_forced) {
      return <StatusBadge tone="warning">Snapshot expired</StatusBadge>;
    }
    const top = item.entries[0];
    return top ? (
      <span>
        <span style={{ color: colors.ink }}>{top.name}</span>{' '}
        <StatusBadge tone="info">{top.source}</StatusBadge>
      </span>
    ) : (
      <span style={{ color: colors.mutedSoft }}>—</span>
    );
  };

  let body: JSX.Element | null = null;
  if (loading && !data) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (data && items.length === 0) {
    body = (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        No open screenings — the sanctions queue is clear.
      </div>
    );
  } else if (data) {
    body = (
      <div className="overflow-hidden rounded-2xl" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">{t('Subject', 'person')}</th>
              <th className="text-left font-medium px-4 py-3">Strongest match</th>
              <th className="text-right font-medium px-4 py-3">Screenings</th>
              <th className="text-right font-medium px-4 py-3">Score</th>
              <th className="text-left font-medium px-4 py-3">Last screened</th>
              <th className="text-right font-medium px-4 py-3">Review</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <Fragment key={item.key}>
                <tr
                  style={{
                    borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}`,
                    opacity: item.is_stale_forced ? 0.72 : 1,
                  }}
                >
                  <td className="px-4 py-3" style={{ color: colors.ink }}>
                    {item.subject_type === 'client' && item.subject_id ? (
                      <Link
                        to={`/manager/clients/${item.subject_id}`}
                        className="hover:underline"
                        style={{ color: colors.ink }}
                      >
                        {item.subject_name}
                      </Link>
                    ) : (
                      item.subject_name
                    )}
                    <div className="text-xs flex items-center gap-1" style={{ color: colors.mutedSoft }}>
                      {item.subject_type}
                      {item.is_escalated && <StatusBadge tone="warning">Escalated</StatusBadge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">{strongestMatchCell(item)}</td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                    {item.screening_count}
                  </td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                    {item.is_stale_forced ? '—' : item.top_score.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                    {formatDateTime(item.last_screened_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-expanded={expanded === item.key}
                      onClick={() => setExpanded(expanded === item.key ? null : item.key)}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
                    >
                      {expanded === item.key ? 'Hide' : 'Review'}
                    </button>
                  </td>
                </tr>
                {expanded === item.key && (
                  <tr>
                    <td colSpan={6} style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
                      <ItemDetail item={item} onAction={setTarget} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Sanctions screening"
        subtitle="Potential matches against the OFAC, United Nations and Canadian sanctions lists. Clearing a false positive needs two distinct reviewers; confirming a true match suspends the client's account immediately, so it is gated. A review forced open by a stale list snapshot cannot be confirmed a true match."
      />

      <div className="mb-3 text-xs" style={{ color: colors.mutedSoft }}>
        {data
          ? `${data.total_items} items · ${data.total_screenings} screenings · page ${page} of ${totalPages}`
          : ''}
      </div>

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.successText }}
        >
          {flash}
        </div>
      )}
      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {body}

      {data && totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => { setExpanded(null); setPage((p) => Math.max(1, p - 1)); }}
            disabled={page <= 1 || loading}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
              opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            ← Previous
          </button>
          <button
            type="button"
            onClick={() => { setExpanded(null); setPage((p) => Math.min(totalPages, p + 1)); }}
            disabled={page >= totalPages || loading}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
              opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            Next →
          </button>
        </div>
      )}

      {target && (
        <DispositionModal
          target={target}
          onClose={() => setTarget(null)}
          onSubmitted={(msg) => {
            setTarget(null);
            setExpanded(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 7000);
            reload();
          }}
        />
      )}
    </>
  );
}

// ─── Disposition dialog ───────────────────────────────────────────
interface DispositionModalProps {
  target: ActionTarget;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

const DISPOSITION_COPY: Record<SanctionDisposition, { title: string; blurb: string; cta: string }> = {
  proposed_false_positive: {
    title: 'Propose false positive',
    blurb:
      'Records your view that the subject is NOT the listed person. It does not clear the alert — a different reviewer must confirm it (four-eyes).',
    cta: 'Propose false positive',
  },
  false_positive: {
    title: 'Confirm false positive',
    blurb:
      'Clears the screening as not a match. You cannot confirm a false positive you proposed yourself — a different reviewer must.',
    cta: 'Confirm false positive',
  },
  confirmed_true_match: {
    title: 'Confirm true match',
    blurb:
      'Confirms the subject IS the listed person. This SUSPENDS the account immediately (mandatory hold for a listed person), blocks what they hold in a US entity of the group, and starts the 24 hours in which the compliance officer records whether a suspicion report (aviso) was filed. Follow up per the sanctions procedure.',
    cta: 'Confirm true match & suspend',
  },
  escalated: {
    title: 'Escalate to senior compliance',
    blurb:
      'Flags this screening for a senior reviewer (super_admin / Oficial de Cumplimiento) without resolving it. It stays open in the queue, marked Escalated.',
    cta: 'Escalate',
  },
};

function DispositionModal({ target, onClose, onSubmitted }: Readonly<DispositionModalProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { hit, disposition } = target;
  const copy = DISPOSITION_COPY[disposition];
  const isTrueMatch = disposition === 'confirmed_true_match';

  const reasonOk = reason.trim().length >= 1 && reason.trim().length <= 2000;
  const phraseOk = !isTrueMatch || confirmPhrase.trim() === SUSPENSION_PHRASE;
  const valid = reasonOk && phraseOk;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await sanctionsService.disposition(hit.result_id, disposition, reason.trim());
      onSubmitted(dispositionMessage(disposition, hit.subject_name, res.account_suspended));
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>{copy.title}</h2>
      <p className="text-sm mb-4" style={{ color: isTrueMatch ? colors.danger : colors.muted }}>
        {copy.blurb}
      </p>

      <div className="mb-4 p-3 rounded-lg text-xs" style={{ background: colors.surfaceMuted, color: colors.textPrimary }}>
        <div className="font-semibold">{hit.subject_name}</div>
        <div style={{ color: colors.muted }}>
          {hit.subject_type} · {hit.context}
          {hit.matches[0] && ` · strongest: ${hit.matches[0].name} (${hit.matches[0].source})`}
        </div>
      </div>

      <label htmlFor="disp-reason" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
        Reason (kept in the audit log)
      </label>
      <textarea
        id="disp-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4"
        style={{ border: `1px solid ${colors.border}` }}
      />

      {isTrueMatch && (
        <div className="mb-4">
          <label htmlFor="disp-phrase" className="block text-xs font-medium mb-1" style={{ color: colors.danger }}>
            {t('Type', 'action')} <span className="font-mono" translate="no">{SUSPENSION_PHRASE}</span> to enable suspension
          </label>
          <input
            id="disp-phrase"
            type="text"
            value={confirmPhrase}
            onChange={(e) => setConfirmPhrase(e.target.value)}
            autoComplete="off"
            className="w-full px-3 py-2 rounded-lg text-sm font-mono"
            style={{ border: `1px solid ${phraseOk ? colors.dangerBorder : colors.border}` }}
          />
        </div>
      )}

      {err && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!valid || busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: isTrueMatch ? colors.danger : gradients.gold,
            color: isTrueMatch ? '#fff' : colors.navy,
            opacity: valid && !busy ? 1 : 0.5,
          }}
        >
          {busy ? 'Saving…' : copy.cta}
        </button>
      </div>
    </Modal>
  );
}
